/**
 * Shared TypeScript type definitions for the observability feature.
 *
 * STATUS_META is the single source of truth for all status attributes.
 * Adding a new status is one line in one place — the compiler forces
 * label and color to both be filled in.
 */

export const STATUS_META = {
  healthy: { label: 'Operational', color: '#1d8102' },
  disrupted: { label: 'Disrupted', color: '#d13212' },
} as const;

export type HealthStatus = keyof typeof STATUS_META;

// Fallback guards against BFF returning a status not yet known to the UI.
export const statusToLabel = (status: HealthStatus): string =>
  STATUS_META[status]?.label ?? 'Unknown';

export interface DayHealth {
  date: string;
  status: HealthStatus;
}

/** Error types returned by data-fetching hooks. */
export type FetchErrorKind = 'auth' | 'network' | 'unknown';

/** Maps a rejected AtmsApiClient request to the error kind the page renders. */
export function toFetchErrorKind(err: { status?: number; response?: { status?: number } }): FetchErrorKind {
  const status = err?.status ?? err?.response?.status;
  if (status === 401 || status === 403) return 'auth';
  return status ? 'unknown' : 'network';
}

/** Shared state shape for alarm history hooks. */
export interface AlarmHistoryHookState {
  isLoading: boolean;
  data: CategoryAlarmHistoryResponse | null;
  error: FetchErrorKind | null;
  lastUpdated: Date | null;
}

/**
 * Response from GET /api/observability/categoryAlarmHistory and
 * GET /api/observability/overall-alarm-history.
 *
 * The BFF queries composite alarms (one per category) and returns
 * pre-rolled-up health data — no per-alarm detail needed.
 */
export interface CategoryAlarmHistoryResponse {
  overallStatus: HealthStatus;
  history: DayHealth[];
}

/** CloudWatch's alarm states, plus 'MISSING' when a composite rule names an alarm that does not exist. */
export type AlarmStateValue = 'OK' | 'ALARM' | 'INSUFFICIENT_DATA' | 'MISSING';

/** One CloudWatch alarm in dev mode. */
export interface AlarmDetail {
  name: string;
  state: AlarmStateValue;
  since: string | null;
  reason: string;
}

/** Response from GET /api/observability/alarm-details, which the BFF serves to sysadmins only. */
export interface AlarmDetailsResponse {
  categories: { category: string; alarms: AlarmDetail[] }[];
  otherFiring: AlarmDetail[];
  /** True when more alarms were firing than the BFF's bounded account-wide query reads. */
  otherFiringTruncated: boolean;
  fetchedAt: string;
}
